<template>
  <div
    class="inline-flex items-center relative"
    :class="{
      'pe-input': !simple,
      'pe-input-simple': simple,
      error: !!error,
      disabled
    }"
  >
    <template v-if="hasPrefix">
      <slot name="prefix" class="pl-3" />
    </template>
    <template v-if="type !== 'textarea'">
      <input
        v-if="type !== 'textarea'"
        v-model="value"
        :type="type"
        :placeholder="placeholder"
        :disabled="disabled"
        v-maska
        :data-maska="cMask"
        class="pe-input-content w-full grow text-inherit"
        :class="inputClass"
      />
      <span v-if="type !== 'number' && !disabled && countCharacters" class="pr-[14px] italic text-xs text-gray-500 dark:text-neutral-200 flex">{{ currentLength }} <span v-if="hasMaxLength">/{{ maxlength }}</span></span>
    </template>
    <template v-else>
      <textarea
        v-model="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :rows="rows"
        class="pe-input-content h-full grow resize-none"
        :class="inputClass"
      />
      <span v-if="!disabled && countCharacters" class="text-length">{{ currentLength }}<span v-if="hasMaxLength">/{{ maxlength }}</span></span>
    </template>
    <template v-if="hasSuffix">
      <slot name="suffix" class="pr-4" />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue'


const props = withDefaults(
  defineProps<{
    modelValue?: string | number
    placeholder?: string
    prefix?: string
    suffix?: string
    type?: string
    textTransform?: string
    disabled?: boolean
    maxlength?: number | string
    simple?: boolean
    rows?: number | string
    inputClass?: string | [] | {}
    error?: any
    mask?: string | string[]
    countCharacters?: boolean
  }>(),
  {
    countCharacters: true
  }
)

const slots = useSlots()
const emit = defineEmits(['update:modelValue'])

const currentLength = computed(() => {
  if (props.modelValue != null && props.modelValue != undefined) {
    return `${String(props.modelValue).length}` 
  }
  return 0
})
const value = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

const hasPrefix = computed(() => !!slots.prefix || !!props.prefix || false)
const hasSuffix = computed(() => !!slots.suffix || !!props.suffix || false)
const hasMaxLength = computed(() => Number(props.maxlength) >= 0)

const cMask = computed(() => (Array.isArray(props.mask) ? JSON.stringify(props.mask) : props.mask))
</script>

<style lang="scss" scoped>
.pe-input-content {
  // O padding tem valor diferente do normal (x4) aqui para compensar as bordas
  @apply box-border border-0 bg-transparent py-[14px] px-4 text-base font-normal leading-4 outline-none dark:text-neutral-200;
}

.text-length {
  @apply italic text-xs text-gray-500 dark:text-neutral-200 dark:bg-slate-800/60 absolute right-[14px] bottom-2 z-10 bg-white p-1 rounded-md;
}

.pe-input {
  @apply box-border rounded-lg border border-solid border-black transition-all dark:border-neutral-600 dark:text-white dark:placeholder:text-neutral-200;
  input,
  textarea {
    @apply placeholder:italic;
  }

  &:not(.disabled, .error) {
    @apply hover:border-transparent hover:bg-[#00000014] dark:hover:border-white dark:hover:bg-[#ffffff29];
    > input {
      @apply dark:text-white;
    }
  }
  &.disabled {
    @apply border-[#00000052] text-[#00000052] dark:border-neutral-700;

    input {
      @apply placeholder:text-[#00000052];
    }
  }

  &:focus-within {
    @apply border-black dark:border-neutral-600 dark:bg-[#ffffff29];
  }

  &.error {
    @apply border-red-400;
  }
}
.pe-input-simple {
  @apply box-border border-2 border-solid border-inherit border-x-transparent border-t-transparent;

  .pe-input-content {
    @apply placeholder:italic placeholder:text-inherit placeholder:opacity-50;
  }
}

.error {
  .pe-input {
    @apply border-red-400 bg-red-50 dark:bg-transparent;
  }
}

.success {
  .pe-input {
    @apply border-emerald-300 bg-emerald-50 dark:bg-transparent;
  }
}

input:-webkit-autofill,
input:-webkit-autofill:focus {
  transition: background-color 600000s 0s, color 600000s 0s;
}
input[data-autocompleted] {
  background-color: transparent !important;
}

*::-webkit-scrollbar {
  width: 5px;
}

*::-webkit-scrollbar-track {
  background: transparent;
}

*::-webkit-scrollbar-thumb {
  background-color: #a8a8a8;
  border-radius: 4px;
}
</style>
